import type { ServerSideSelectionState } from '@civilgrid/ui/components';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useMemo, useState } from 'react';
import type { BulkUpdateSelection, CivilSearcherResponse } from '~/api/civilsearch';
import type { DataRequestV2 } from '~/common/types';
import { Notify } from '~/components2/Notify';
import { useBulkUpdateDataRequests } from '~/hooks/civilsearch/useDataRequest';

export type AssigneeOption = { value: string; label: string };

export type UseBulkReassignParams = {
  civilSearchers: CivilSearcherResponse[];
  buildSearchPayloadForBulk: () => { [key: string]: any };
  getActiveQueryKeyParams: () => object;
  getDataRequestById?: (id: string) => DataRequestV2 | undefined;
};

export function useBulkReassign({
  civilSearchers,
  buildSearchPayloadForBulk,
  getActiveQueryKeyParams,
  getDataRequestById,
}: UseBulkReassignParams) {
  const queryClient = useQueryClient();
  const [pendingSelection, setPendingSelection] = useState<ServerSideSelectionState | null>(null);
  const [assigneeId, setAssigneeId] = useState<string>('');
  const mutation = useBulkUpdateDataRequests();

  const assigneeOptions: AssigneeOption[] = useMemo(
    () =>
      (civilSearchers ?? [])
        .filter((s) => s.status === 'active')
        .map((s) => ({
          value: s.userId,
          label: s.name || s.email || '',
        })),
    [civilSearchers],
  );

  const open = useCallback(
    (selection: ServerSideSelectionState) => {
      setPendingSelection(selection);
      const isSingleRequest = selection.mode === 'partial' && selection.selectedIds.length === 1 && getDataRequestById;
      if (isSingleRequest) {
        const request = getDataRequestById(selection.selectedIds[0]);
        if (request?.assignedTo?.id) {
          setAssigneeId(request.assignedTo.id);
          return;
        }
      }
      setAssigneeId('');
    },
    [getDataRequestById],
  );

  const close = useCallback(() => {
    setPendingSelection(null);
  }, []);

  const onSubmit = useCallback(() => {
    if (!pendingSelection || !assigneeId) return;

    const selection: BulkUpdateSelection =
      pendingSelection.mode === 'all' ? { ...pendingSelection, search: buildSearchPayloadForBulk() } : pendingSelection;

    const searcher = civilSearchers.find((s) => s.userId === assigneeId);

    const activeQueryKey = ['data-requests-v2', getActiveQueryKeyParams()];

    mutation.mutate(
      { selection, updates: { assignedToId: assigneeId } },
      {
        onSuccess: () => {
          setPendingSelection(null);
          queryClient.setQueryData<{ requests: DataRequestV2[] }>(activeQueryKey, (old) => {
            if (!old) return old;
            const assignedTo = searcher
              ? ({ id: assigneeId, name: searcher.name, email: searcher.email } as DataRequestV2['assignedTo'])
              : undefined;
            return {
              ...old,
              requests: old.requests.map((row) => {
                const affected =
                  selection.mode === 'partial'
                    ? selection.selectedIds.includes(row.id)
                    : !selection.excludedIds.includes(row.id);
                if (!affected || !assignedTo) return row;
                return { ...row, assignedTo };
              }),
            };
          });
        },
        onError: () => {
          Notify.error({ message: 'Failed to reassign requests. Please try again.' });
        },
      },
    );
  }, [
    pendingSelection,
    assigneeId,
    civilSearchers,
    buildSearchPayloadForBulk,
    getActiveQueryKeyParams,
    mutation,
    queryClient,
  ]);

  return {
    isOpen: pendingSelection !== null,
    open,
    close,
    assigneeId,
    setAssigneeId,
    assigneeOptions,
    onSubmit,
    isPending: mutation.isPending,
  };
}
